/**
 * 标签栏动态路由工具函数
 *
 * 提供路由匹配、ID格式化、标题生成等功能
 */

import { DynamicRouteConfig } from '@/types/menu'
import { DYNAMIC_ROUTE_CONFIGS } from './dynamic-routes'

/**
 * 路由匹配结果接口
 */
interface RouteMatchResult {
  matched: boolean
  params: Record<string, string>
}

/**
 * 匹配动态路由模式
 *
 * @example
 * matchRoute('/orders/123', '/orders/:id')
 * => { matched: true, params: { id: '123' } }
 *
 * @param pathname - 当前路径（如 /orders/123）
 * @param pattern - 路由模式（如 /orders/:id）
 * @returns 匹配结果和参数对象
 */
export function matchRoute(pathname: string, pattern: string): RouteMatchResult {
  // 分割路径和模式为段数组
  const pathSegments = pathname.split('/').filter(Boolean)
  const patternSegments = pattern.split('/').filter(Boolean)

  // 段数量不同，直接返回不匹配
  if (pathSegments.length !== patternSegments.length) {
    return { matched: false, params: {} }
  }

  const params: Record<string, string> = {}

  // 逐段比对
  for (let i = 0; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i]
    const pathSegment = pathSegments[i]

    // 动态段（以 : 开头）
    if (patternSegment.startsWith(':')) {
      const paramName = patternSegment.slice(1) // 移除 :
      params[paramName] = pathSegment
    }
    // 静态段必须完全匹配
    else if (patternSegment !== pathSegment) {
      return { matched: false, params: {} }
    }
  }

  return { matched: true, params }
}

/**
 * 格式化长ID（雪花ID截断）
 *
 * @example
 * formatId('144016537244798976', 12) => "14401653...76"
 * formatId('123', 12) => "123"
 *
 * @param id - 原始ID
 * @param maxLength - 最大长度（默认12）
 * @returns 格式化后的ID
 */
export function formatId(id: string, maxLength: number = 12): string {
  if (id.length <= maxLength) return id

  // 前60% + "..." + 后(40%-3)字符
  const frontChars = Math.floor(maxLength * 0.6)
  const backChars = maxLength - frontChars - 3
  return `${id.slice(0, frontChars)}...${id.slice(-backChars)}`
}

/**
 * 根据模板和参数生成标题
 *
 * @example
 * generateTitle('订单 #{id}', { id: '144016537244798976' })
 * => "订单 #14401653...76"
 *
 * @param template - 标题模板（如 "订单 #{id}"）
 * @param params - 参数对象
 * @returns 生成的标题
 */
export function generateTitle(
  template: string,
  params: Record<string, string>
): string {
  let title = template

  // 替换所有占位符 {paramName}，保留 # 符号
  for (const [key, value] of Object.entries(params)) {
    const placeholder = `{${key}}`
    if (title.includes(placeholder)) {
      // 格式化ID（如果值很长）
      const formattedValue = formatId(value)
      title = title.replace(placeholder, formattedValue)
    }
  }

  return title
}

/**
 * 动态路由匹配结果接口
 */
interface DynamicRouteMatch {
  config: DynamicRouteConfig
  params: Record<string, string>
}

/**
 * 查找匹配的动态路由配置
 *
 * 配置按路径长度降序排列，优先匹配长路径
 *
 * @param pathname - 当前路径
 * @returns 匹配的配置和参数，未匹配返回null
 */
export function findDynamicRouteMatch(pathname: string): DynamicRouteMatch | null {
  // 遍历所有动态路由配置（已按长度降序排列）
  for (const config of DYNAMIC_ROUTE_CONFIGS) {
    const { matched, params } = matchRoute(pathname, config.pattern)

    if (matched) {
      return { config, params }
    }
  }

  return null
}
